// src/pages/modules/SubcategoryPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchSubcategories,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory,
  clearError,
  exportSubcategories,
  importPreview,
  importConfirm,
} from '../../store/slices/subcategorySlice';
import { fetchCategories } from '../../store/slices/categorySlice';
import { Pencil, Trash2, MoreHorizontal, Loader2, MoreVertical } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import PageHeader from '../../components/common/PageHeader';
import FormField from '../../components/common/FormField';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { usePermissions } from '../../hooks/usePermissions';
import usePermissionSocket from '../../hooks/usePermissionSocket';
import { restoreRole } from 'store/slices/commonSlice';
import { Input } from '../../components/ui/input';

const SubcategoryPage = () => {
  usePermissionSocket();

  const dispatch = useDispatch();
  const { subcategories, pagination, isLoading, error } = useSelector((s) => s.subcategory);
  const { categories } = useSelector((s) => s.category);

  const { canCreate, canUpdate, canDelete, canExport, canImport, canRead } = usePermissions('sub_category');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const [form, setForm] = useState({
    name: '',
    category: '',
    tat: '',
    is_active: true,
    can_assign: true,
    can_issuance: true,
    can_ticket: true,
    buffer_stock_alert: 0,
  });

  const [formErrors, setFormErrors] = useState({});
  const [serverError, setServerError] = useState('');

  const refetch = useCallback(() => {
    dispatch(fetchSubcategories({ page, pageSize, search: searchTerm }));
  }, [dispatch, page, pageSize, searchTerm]);

  useEffect(() => {
    dispatch(fetchCategories({ page: 1, pageSize: 100 }));
  }, [dispatch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      const msg = error.non_field_errors?.[0] || error.name?.[0] || error.detail || 'Operation failed';
      toast.error(msg);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name required';
    if (!form.category) e.category = 'Select category';
    if (!form.tat) e.tat = 'TAT required';
    if (form.buffer_stock_alert < 0) e.buffer_stock_alert = 'Must be 0 or positive';
    setFormErrors(e);
    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const openDialog = (sub = null) => {
    if (sub) {
      if (!canUpdate) return toast.error("You don't have permission to edit");
      setCurrent(sub);
      setForm({
        name: sub.name,
        category: sub.category,
        tat: sub.tat,
        is_active: sub.is_active,
        can_assign: sub.can_assign ?? true,
        can_issuance: sub.can_issuance ?? true,
        can_ticket: sub.can_ticket ?? true,
        buffer_stock_alert: sub.buffer_stock_alert ?? 0,
      });
    } else {
      if (!canCreate) return toast.error("You don't have permission to create");
      setCurrent(null);
      setForm({
        name: '',
        category: '',
        tat: '',
        is_active: true,
        can_assign: true,
        can_issuance: true,
        can_ticket: true,
        buffer_stock_alert: 0,
      });
    }
    setFormErrors({});
    setServerError('');
    setIsDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setServerError('');
    try {
      if (current) {
        await dispatch(updateSubcategory({ id: current.id, ...form })).unwrap();
        toast.success('Subcategory updated');
      } else {
        await dispatch(createSubcategory(form)).unwrap();
        toast.success('Subcategory created');
      }
      setIsDialogOpen(false);
      refetch();
    } catch (err) {
      if (err.non_field_errors) {
        setServerError(err.non_field_errors[0]);
      } else {
        toast.error('Operation failed');
      }
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return toast.error("You don't have permission to delete");
    try {
      await dispatch(deleteSubcategory(current.id)).unwrap();
      toast.success('Subcategory deleted');
      setIsDeleteOpen(false);
      refetch();
    } catch { }
  };

  const getCatName = (id) => categories.find((c) => c.id === id)?.name || '—';

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // FIXED: These functions were missing!
  const handleImportPreview = async (file) => {
    if (!canImport) throw new Error("You don't have import permission");
    const result = await dispatch(importPreview(file)).unwrap();
    return result;
  };

  const handleImportConfirm = async (preview) => {
    if (!canImport) throw new Error("You don't have import permission");
    await dispatch(importConfirm(preview)).unwrap();
    refetch(); // Refresh list after successful import
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Subcategory Name',
      cell: ({ row }) => <span className="font-medium">{row.original.name ?? "-"}</span>,
    },
    {
      header: 'Category Name',
      cell: ({ row }) => <span className="text-sm">{getCatName(row.original.category ?? "-")}</span>,
    },
    {
      accessorKey: 'tat',
      header: 'TAT',
      cell: ({ row }) => <Badge variant="outline">{row.original.tat}</Badge>,
    },
    // {
    //   accessorKey: 'buffer_stock_alert',
    //   header: 'Buffer Stock',
    //   cell: ({ row }) => <Badge variant="secondary">{row.original.buffer_stock_alert ?? "-"}</Badge>,
    // },
    {
      header: 'Current Permissions',
      cell: ({ row }) => {
        const s = row.original;
        return (
          <div className="flex gap-2 flex-wrap">
            <Badge variant={s.can_assign ? 'success' : 'secondary'}>Assign</Badge>
            <Badge variant={s.can_issuance ? 'success' : 'secondary'}>Issuance</Badge>
            <Badge variant={s.can_ticket ? 'success' : 'secondary'}>Ticket</Badge>
          </div>
        );
      },
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? 'success' : 'secondary'}>
          {row.original.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created On',
      cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.created_at) ?? '-'}</span>,
    },
    {
      accessorKey: 'created_by_name',
      header: 'Created By',
      cell: ({ row }) => <span className="text-sm">{row.original.created_by_name ?? '-'}</span>,
    },

    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const s = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canUpdate && (
                <DropdownMenuItem onClick={() => openDialog(s)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => {
                    setCurrent(s);
                    setIsDeleteOpen(true);
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Subcategories"
        description="Manage subcategories with TAT & permissions"
        action={
          canCreate
            ? { label: 'Add Subcategory', onClick: () => openDialog() }
            : null
        }
      />

      {/* {isLoading && !subcategories.length && (
        <div className="flex justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading subcategories...</span>
        </div>
      )} */}

      {canRead ? (
        <DataTable
          data={subcategories}
          totalCount={pagination.count}
          columns={columns}
          templateUrl="/import-template/sub.csv"
          templateDownloadName="subcategory-import-template.csv"
          templateKey="subcategory"
          searchPlaceholder="Search subcategories..."
          showExport={canExport}
          onExport={canExport ? () => dispatch(exportSubcategories()) : undefined}
          showImport={canImport}
          onImportPreview={canImport ? handleImportPreview : undefined}
          onImportConfirm={canImport ? handleImportConfirm : undefined}
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={Math.ceil(pagination.count / pagination.page_size)}
          onPageChange={(i) => setPage(i + 1)}
          onPageSizeChange={setPageSize}
          onSearch={setSearchTerm}
          isLoading={isLoading}
          onDelete={async (id) => {
            await dispatch(deleteSubcategory(id));
            refetch();
          }}
          onRestore={async (id) => {
            await dispatch(restoreRole({ entityType: 'subcategories', id }));
            refetch();
          }}
        />
      ) : (
        <p className="text-center text-muted-foreground">No access</p>
      )}

      {/* ADD/EDIT DIALOG */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{current ? 'Edit Subcategory' : 'Add Subcategory'}</DialogTitle>
            <DialogDescription>
              {current ? 'Update details' : 'Create a new subcategory'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Name"
                name="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                error={formErrors.name}
                required
                placeholder="e.g., Laptops"
              />
              <FormField
                label="Category"
                name="category"
                type="select"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                error={formErrors.category}
                required
                options={categories.map((c) => ({ value: c.id, label: c.name }))}
              />
            </div>

            {/* <FormField
              label="TAT (Turn around Time)"
              name="tat"
              type="time"
              step="1"
              value={form.tat}
              onChange={(e) => setForm({ ...form, tat: e.target.value })}
              error={formErrors.tat}
              required
              placeholder="e.g., 02:30 or 1 12:12:12"
            /> */}
            {/* <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-3">
                <Label>Turn around Time (TAT) - Day : HH:MM:SS </Label>
                <div className='flex gap-3'>
           
                  <FormField
                    // label="Days"
                    name="tat_days_temp"
                    type="number"
                    min={0}
                    value={form.tat?.split(" ")[0] || 0}  // extract days
                    onChange={(e) => {
                      const days = e.target.value;
                      const time = form.tat?.split(" ")[1] || "00:00:00";
                      setForm({ ...form, tat: `${days} ${time}` });
                    }}
                    placeholder="0"
                    className="max-w-[135px]"
                  // required
                  />
                  <span>:</span>
                
                  <FormField
                    // label="TAT (Turn around Time)"
                    name="tat_time_temp"
                    type="time"
                    step="1"
                    value={form.tat?.split(" ")[1] || ""}  // extract time
                    onChange={(e) => {
                      const time = e.target.value;
                      const days = form.tat?.split(" ")[0] || 0;
                      setForm({ ...form, tat: `${days} ${time}` });
                    }}
                    required
                  />

                </div>
              </div>
              <div className="flex flex-col gap-3">
                <Label>Buffer Stock (Qty) </Label>
                <FormField
                  // label="Buffer Stock (Qty)"
                  name="buffer_stock_alert"
                  type="number"
                  min="0"
                  value={form.buffer_stock_alert}
                  onChange={(e) => setForm({ ...form, buffer_stock_alert: Number(e.target.value) })}
                  error={formErrors.buffer_stock_alert}
                  placeholder="0"
                  helperText="Alert when stock falls below this number"
                />
              </div>
            </div> */}

            {/* </div> */}


            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-3">
                <Label>Turn Around Time (TAT) - Days : HH:MM:SS</Label>
                <div className="flex items-center gap-3">
                  {/* Days Input */}
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={
                      // Extract days: if tat has space, take first part; else default to 0
                      form.tat && form.tat.includes(' ')
                        ? form.tat.split(' ')[0]
                        : '0'
                    }
                    onChange={(e) => {
                      const days = e.target.value || '0';
                      const timePart = form.tat && form.tat.includes(' ')
                        ? form.tat.split(' ')[1]
                        : (form.tat || '00:00:00');
                      setForm({ ...form, tat: `${days} ${timePart}` });
                    }}
                    className="w-[135px]"
                  />
                  <span className="text-muted-foreground">:</span>
                  {/* Time Input */}
                  <FormField
                    type="time"
                    step="1"
                    value={
                      // Extract time: second part if space exists, else full tat or default
                      form.tat && form.tat.includes(' ')
                        ? form.tat.split(' ')[1]
                        : (form.tat || '00:00:00')
                    }
                    onChange={(e) => {
                      const time = e.target.value || '00:00:00';
                      const days = form.tat && form.tat.includes(' ')
                        ? form.tat.split(' ')[0]
                        : '0';
                      setForm({ ...form, tat: `${days} ${time}` });
                    }}
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Current saved value: <code className="bg-muted px-1 rounded">{form.tat || 'Not set'}</code>
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <Label>Buffer Stock Alert (Qty)</Label>
                <Input
                  type="text"
                  min="0"
                  placeholder="0"
                  value={form.buffer_stock_alert ?? 0}
                  onChange={(e) => setForm({ ...form, buffer_stock_alert: Number(e.target.value) || 0 })}
                  helperText="Alert when stock falls below this number"
                />
              </div>
            </div>





            <div className="space-y-4 py-4 border-t">
              <h4 className="font-medium">Permissions</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center space-x-3">
                  <Switch
                    checked={form.can_assign}
                    onCheckedChange={(v) => setForm({ ...form, can_assign: v })}
                  />
                  <Label>Can Assign</Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Switch
                    checked={form.can_issuance}
                    onCheckedChange={(v) => setForm({ ...form, can_issuance: v })}
                  />
                  <Label>Can Issuance</Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Switch
                    checked={form.can_ticket}
                    onCheckedChange={(v) => setForm({ ...form, can_ticket: v })}
                  />
                  <Label>Can Create Ticket</Label>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={(c) => setForm({ ...form, is_active: c })}
              />
              <Label>Active Status</Label>
            </div>

            {serverError && <p className="text-sm text-destructive">{serverError}</p>}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {current ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Subcategory?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{current?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SubcategoryPage;